import { Fragment, useEffect, useState } from 'react'
import { fetchEventLog, ENTITY_TYPES, type EventLogEntry, type EntityType } from './api/eventLogApi'
import { Dropdown, PageHeader, Badge, Button, TextInput } from '../../shared/ui'
import { PaginationBar, usePagination } from '../../shared/Pagination'

const PAGE_SIZES = [25, 50, 100, 200] as const
const FETCH_LIMIT = 1000

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'boolean') return v ? 'ja' : 'nein'
  if (typeof v === 'string' || typeof v === 'number') return String(v)
  if (Array.isArray(v)) return `[${v.length} Einträge]`
  if (typeof v === 'object') {
    const keys = Object.keys(v as object)
    return keys.length ? `{${keys.slice(0, 4).join(', ')}${keys.length > 4 ? '…' : ''}}` : '{}'
  }
  return String(v)
}

function DiffView({ diffJson, action }: { diffJson: string; action: string }) {
  let parsed: unknown
  try { parsed = JSON.parse(diffJson) } catch { return <pre className="text-[11px] text-slate-600">{diffJson}</pre> }
  if (!parsed || typeof parsed !== 'object') return <pre className="text-[11px] text-slate-600">{String(parsed)}</pre>

  const obj = parsed as Record<string, unknown>

  // Update-style: { field: { before, after } }
  const changes = Object.entries(obj)
    .filter(([, v]) => v != null && typeof v === 'object' && 'before' in (v as object) && 'after' in (v as object))
    .map(([k, v]) => ({ field: k, ...(v as { before: unknown; after: unknown }) }))

  if (changes.length > 0) {
    return (
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-100 text-slate-500">
            <tr>
              <th className="px-3 py-1.5 text-left font-medium w-1/3">Feld</th>
              <th className="px-3 py-1.5 text-left font-medium">Vorher</th>
              <th className="px-3 py-1.5 text-left font-medium">Nachher</th>
            </tr>
          </thead>
          <tbody>
            {changes.map(c => (
              <tr key={c.field} className="border-t border-slate-100">
                <td className="px-3 py-1.5 font-medium text-slate-700">{c.field}</td>
                <td className="px-3 py-1.5 tabular-nums text-rose-700">{formatValue(c.before)}</td>
                <td className="px-3 py-1.5 tabular-nums text-emerald-700">{formatValue(c.after)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  // Create/delete-style: flat { field: value } OR { after: {...} } / { before: {...} } envelope
  const target = (obj.after && typeof obj.after === 'object' ? obj.after : obj.before && typeof obj.before === 'object' ? obj.before : obj) as Record<string, unknown>
  const rows = Object.entries(target).filter(([k]) => !['before', 'after'].includes(k))
  if (!rows.length) return <p className="text-[11px] text-slate-400">Keine Details erfasst.</p>

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="bg-slate-100 text-slate-500 px-3 py-1.5 text-[10px] uppercase tracking-wider font-medium">
        {action === 'delete' ? 'Gelöschte Werte' : action === 'create' ? 'Erstellte Werte' : 'Details'}
      </div>
      <table className="w-full text-[11px]">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} className="border-t border-slate-100">
              <td className="px-3 py-1.5 font-medium text-slate-700 w-1/3">{k}</td>
              <td className="px-3 py-1.5 text-slate-600">{formatValue(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function EventLogPage() {
  const [rows, setRows] = useState<EventLogEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)

  const [entityType, setEntityType] = useState<EntityType>('')
  const [entityId, setEntityId] = useState('')
  const [actor, setActor] = useState('')

  const { page, setPage, size, setSize, total, totalPages, pageItems } = usePagination(rows ?? [], 50)

  function load() {
    setLoading(true)
    setError(null)
    setPage(0)
    fetchEventLog({
      entityType: entityType || undefined,
      entityId: entityId || undefined,
      actor: actor || undefined,
      limit: FETCH_LIMIT,
    })
      .then(r => setRows(r))
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-6 space-y-5 max-w-6xl">
      <PageHeader
        title={rows ? `${rows.length} Ereignisse` : 'Event Log'}
        subtitle="Audit-Trail aller Schreiboperationen"
      />

      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1 min-w-[180px]">
          <label className="text-[10px] font-medium uppercase tracking-wider text-slate-400">Entity-Typ</label>
          <Dropdown
            size="compact"
            value={entityType}
            onChange={v => setEntityType(v as EntityType)}
            options={ENTITY_TYPES.map(t => ({ value: t, label: t || '— Alle —' }))}
          />
        </div>

        <div className="flex flex-col gap-1 w-32">
          <label className="text-[10px] font-medium uppercase tracking-wider text-slate-400">Entity-ID</label>
          <TextInput
            size="compact"
            value={entityId}
            onChange={e => setEntityId(e.target.value)}
            placeholder="z.B. 42"
          />
        </div>

        <div className="flex flex-col gap-1 w-48">
          <label className="text-[10px] font-medium uppercase tracking-wider text-slate-400">Actor</label>
          <TextInput
            size="compact"
            value={actor}
            onChange={e => setActor(e.target.value)}
            placeholder="E-Mail oder Name"
          />
        </div>

        <Button variant="primary" onClick={load} loading={loading}>
          Suchen
        </Button>
      </div>

      {error && <p className="text-rose-500 text-sm">{error}</p>}

      {rows && rows.length >= FETCH_LIMIT && (
        <div className="px-4 py-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg">
          Maximum {FETCH_LIMIT} Einträge geladen — Filter verfeinern für mehr.
        </div>
      )}

      {rows && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Zeit</th>
                <th className="px-3 py-2 text-left font-medium">Actor</th>
                <th className="px-3 py-2 text-left font-medium">Rolle</th>
                <th className="px-3 py-2 text-left font-medium">Entity</th>
                <th className="px-3 py-2 text-left font-medium">Aktion</th>
                <th className="px-3 py-2 text-left font-medium">Source</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-slate-400">
                    Keine Einträge gefunden.
                  </td>
                </tr>
              )}
              {pageItems.map(r => (
                <Fragment key={r.id}>
                  <tr
                    onClick={() => setExpanded(prev => prev === r.id ? null : r.id)}
                    className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                      expanded === r.id ? 'bg-slate-50' : ''
                    }`}
                  >
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap">
                      {new Date(r.occurred_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'medium' })}
                    </td>
                    <td className="px-3 py-2 max-w-[160px] truncate">{r.actor_user}</td>
                    <td className="px-3 py-2">{r.actor_role ?? '—'}</td>
                    <td className="px-3 py-2">
                      <span className="font-medium">{r.entity_type}</span>
                      <span className="text-slate-400"> #{r.entity_id}</span>
                    </td>
                    <td className="px-3 py-2">
                      <Badge
                        tone={r.action === 'create' ? 'success' : r.action === 'delete' ? 'danger' : 'warn'}
                      >
                        {r.action}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-slate-400">{r.source ?? '—'}</td>
                  </tr>
                  {expanded === r.id && r.diff_json && (
                    <tr className="bg-slate-50 border-t border-slate-100">
                      <td colSpan={6} className="px-4 py-3">
                        <DiffView diffJson={r.diff_json} action={r.action} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          {rows.length > 0 && (
            <PaginationBar
              page={page}
              setPage={setPage}
              size={size}
              setSize={setSize}
              total={total}
              totalPages={totalPages}
              sizes={PAGE_SIZES}
            />
          )}
        </div>
      )}
    </div>
  )
}
